import { useEffect, useState } from 'react';
import { Alert, Button, ButtonGroup, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../components/common/Loader';
import PrescriptionSheet from '../components/print/PrescriptionSheet';
import { consultationApi } from '../api';
import { useToast } from '../context/ToastContext';

export default function PrintPrescription() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paper, setPaper] = useState('A4');
  const [sending, setSending] = useState(false);
  // Once the sheet has been through the printer, the way out is offered.
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    consultationApi
      .printData(visitId)
      .then((res) => {
        setData(res.data);
        setPaper(res.data.clinic.paperSize || 'A4');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [visitId]);

  // The browser's own @page rule decides the sheet size, so it is injected here
  // rather than living in the stylesheet.
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `@media print { @page { size: ${paper}; margin: 0; } }`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [paper]);

  /**
   * Messages the patient, then opens the print dialog. The message goes first
   * so the doctor sees whether it left before the dialog covers the screen;
   * the server skips it on a second printing of the same visit.
   *
   * Leaving for the dashboard is a separate button on purpose. The browser
   * reports nothing about how its dialog was closed - printing and cancelling
   * look identical to the page - so navigating on its own would carry the
   * doctor off the screen every time they cancelled.
   */
  async function sendAndPrint() {
    setSending(true);
    try {
      const res = await consultationApi.notify(visitId);
      const note = res.data;
      if (note.sent) toast.success('WhatsApp message sent to the patient');
      else if (!note.already) toast.warn(`WhatsApp did not go: ${note.reason}`);
    } catch (err) {
      // Never let a messaging problem stop the prescription being printed.
      toast.warn(`WhatsApp did not go: ${err.message}`);
    } finally {
      setSending(false);
      setPrinted(true);
    }

    window.print();
  }

  if (loading) return <Loader label="Preparing the prescription..." className="py-5" />;

  if (error) {
    return (
      <Alert variant="warning" className="text-center">
        <i className="bi bi-exclamation-triangle fs-3 d-block mb-2" />
        {error}
        <div className="mt-3">
          <Button variant="light" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </Alert>
    );
  }

  const sheetData = { ...data, clinic: { ...data.clinic, paperSize: paper } };

  return (
    <>
      {/* Stays put while the sheet scrolls underneath it. */}
      <div className="rx-toolbar no-print">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button variant="light" onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left me-1" /> Back
            </Button>
            <div>
              <div className="fw-bold">{data.patient.name}</div>
              <div className="text-muted small">
                {/* {data.patient.patientId} &middot; Token {data.visit.tokenNo} */}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Form.Select value={paper} onChange={(e) => setPaper(e.target.value)} style={{ width: 110 }}>
              <option value="A4">A4</option>
              <option value="A5">A5</option>
            </Form.Select>

            <ButtonGroup>
              <Button variant="outline-secondary" onClick={() => navigate(`/patients/${data.patient._id}`)}>
                <i className="bi bi-person-lines-fill me-1" /> Record
              </Button>
              <Button className="btn-clinic" onClick={sendAndPrint} disabled={sending}>
                {sending ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <i className="bi bi-printer me-1" />
                )}
                Print
              </Button>
            </ButtonGroup>

            {/* Appears once printing has been tried, so the doctor is never
                carried away from the sheet by a cancelled dialog. */}
            {printed && (
              <Button variant="success" onClick={() => navigate('/dashboard')}>
                <i className="bi bi-check2-circle me-1" /> Done
              </Button>
            )}
          </div>
        </div>

        <div className="alert alert-light border small text-muted mb-0">
          <i className="bi bi-info-circle me-1" />
          In the print dialog set Margins to <strong>None</strong> and turn off "Headers and footers" so the sheet
          matches this preview exactly.
        </div>
      </div>

      {/* The padding is preview-only; on paper it would spill onto a second
          page, so print.css strips it. */}
      <div className="rx-preview">
        <PrescriptionSheet data={sheetData} />
      </div>
    </>
  );
}
